import { useState } from 'react';

export function useEmailPreview(token) {
  const [emailPreview, setEmailPreview] = useState(null);
  // emailPreview: { order, subject, html, to, loading, sending, sent, error } | null

  async function openEmailPreview(order) {
    setEmailPreview({ order, subject: '', html: '', to: order.customer_email, loading: true, sending: false, sent: false, error: null });
    try {
      const res = await fetch('/.netlify/functions/send-status-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ orderId: order.id, preview: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Preview failed');
      setEmailPreview(prev => ({ ...prev, subject: data.subject, html: data.html, loading: false }));
    } catch (err) {
      setEmailPreview(prev => ({ ...prev, loading: false, error: err.message }));
    }
  }

  async function sendEmailFromPreview() {
    if (!emailPreview) return;
    setEmailPreview(prev => ({ ...prev, sending: true, error: null }));
    try {
      const res = await fetch('/.netlify/functions/send-status-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ orderId: emailPreview.order.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Send failed');
      setEmailPreview(prev => ({ ...prev, sending: false, sent: true }));
    } catch (err) {
      setEmailPreview(prev => ({ ...prev, sending: false, error: err.message }));
    }
  }

  return { emailPreview, setEmailPreview, openEmailPreview, sendEmailFromPreview };
}

export function EmailPreviewModal({ emailPreview, setEmailPreview, sendEmailFromPreview, statusLabels }) {
  if (!emailPreview) return null;

  return (
    <div className="modal-overlay" onClick={() => !emailPreview.sending && setEmailPreview(null)}>
      <div className="modal-box email-preview-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>✉ Email Preview</h2>
          <button className="modal-close" onClick={() => setEmailPreview(null)} disabled={emailPreview.sending}>✕</button>
        </div>

        {emailPreview.loading ? (
          <div className="email-preview-loading">Loading preview…</div>
        ) : emailPreview.error && !emailPreview.sent ? (
          <div className="email-preview-error">⚠ {emailPreview.error}</div>
        ) : emailPreview.sent ? (
          <div className="email-preview-sent">
            <span className="email-preview-sent-icon">✓</span>
            <p>Email sent successfully to <strong>{emailPreview.to}</strong></p>
            <button className="email-preview-close-btn" onClick={() => setEmailPreview(null)}>Close</button>
          </div>
        ) : (
          <>
            <div className="email-preview-meta">
              <div className="email-preview-meta-row">
                <span className="email-preview-meta-label">To</span>
                <span className="email-preview-meta-value">{emailPreview.to}</span>
              </div>
              <div className="email-preview-meta-row">
                <span className="email-preview-meta-label">Subject</span>
                <span className="email-preview-meta-value">{emailPreview.subject}</span>
              </div>
              <div className="email-preview-meta-row">
                <span className="email-preview-meta-label">Status</span>
                <span className="email-preview-meta-value" style={{ color: statusLabels[emailPreview.order.status]?.color, fontWeight: 600 }}>
                  {statusLabels[emailPreview.order.status]?.label}
                </span>
              </div>
            </div>
            <div className="email-preview-frame-wrap">
              <iframe
                className="email-preview-frame"
                title="Email Preview"
                srcDoc={emailPreview.html}
                sandbox="allow-same-origin allow-popups allow-top-navigation"
              />
            </div>
            <div className="email-preview-actions">
              <button className="email-preview-cancel-btn" onClick={() => setEmailPreview(null)}>Cancel</button>
              <button
                className="email-preview-send-btn"
                onClick={sendEmailFromPreview}
                disabled={emailPreview.sending}
              >
                {emailPreview.sending ? 'Sending…' : `Send to ${emailPreview.to}`}
              </button>
            </div>
            {emailPreview.error && (
              <p className="email-preview-send-error">⚠ {emailPreview.error}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
